import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AuthSession } from "@websede/contracts";
import { api, forgetSession } from "../api/client.js";
import { disablePushBeforeSignOut } from "../push.js";
import { rememberUser } from "../pwa.js";

export const sessionQuery = queryOptions({
  queryKey: ["auth", "me"] as const,
  queryFn: async () => {
    const session = await api.auth.me();
    rememberUser(session?.user.id ?? null);
    return session;
  },
  staleTime: 60_000,
  retry: false,
});

export function useSession() {
  return useQuery(sessionQuery);
}

/** Puts a freshly returned session into the cache so guards see it without a round trip. */
export function setSession(queryClient: QueryClient, session: AuthSession | null): void {
  queryClient.setQueryData(sessionQuery.queryKey, session);
  rememberUser(session?.user.id ?? null);
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await disablePushBeforeSignOut();
      return api.auth.logout();
    },
    onSettled: () => {
      forgetSession();
      setSession(queryClient, null);
      queryClient.removeQueries({ queryKey: ["auth"] });
      queryClient.removeQueries({ queryKey: ["invites"] });
    },
  });
}

/** Where a signed-in user should land, given the state of their second factor. */
export function landingFor(session: AuthSession): "/mfa" | "/mail" {
  return session.mfa.pending ? "/mfa" : "/mail";
}
